import { lazy, ComponentType } from "react";

/**
 * Wraps React.lazy() with automatic retry logic for failed dynamic imports.
 * When a deployment happens, old chunk filenames no longer exist on the server.
 * This helper:
 * 1. Retries the import twice (in case of network blip)
 * 2. If still failing, clears all service worker caches and reloads ONCE
 * 3. Uses sessionStorage to prevent infinite reload loops
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  maxRetries = 2,
  retryDelay = 1000
) {
  return lazy(() => retryImport(importFn, maxRetries, retryDelay));
}

const RELOAD_KEY = "smiths_chunk_reload_v2";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function retryImport<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  maxRetries: number,
  retryDelay: number
): Promise<{ default: T }> {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const module = await importFn();
      // Success — clear any reload flag
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.removeItem(RELOAD_KEY);
      }
      return module;
    } catch (error) {
      if (attempt === maxRetries) {
        // All retries exhausted — clear SW caches and reload ONCE
        if (typeof window !== "undefined" && typeof sessionStorage !== "undefined") {
          const hasReloaded = sessionStorage.getItem(RELOAD_KEY);
          if (!hasReloaded) {
            // Mark that we're reloading to prevent infinite loops
            sessionStorage.setItem(RELOAD_KEY, "1");

            // Clear all service worker caches first
            try {
              if ("caches" in window) {
                const keys = await caches.keys();
                await Promise.all(keys.map((k) => caches.delete(k)));
              }
              // Tell the service worker to clear caches too
              if (navigator.serviceWorker?.controller) {
                navigator.serviceWorker.controller.postMessage("CLEAR_ALL_CACHES");
              }
              // Unregister the service worker to prevent it from serving stale content
              const registrations = await navigator.serviceWorker?.getRegistrations();
              if (registrations) {
                await Promise.all(registrations.map((r) => r.unregister()));
              }
            } catch {
              // Ignore errors during cache clearing
            }

            // Small delay to let cache clearing complete
            await new Promise((resolve) => setTimeout(resolve, 200));

            // Force reload from server (bypass cache)
            window.location.reload();
            // Return a never-resolving promise to prevent React from rendering an error
            return new Promise(() => {});
          }
          // Already reloaded once — clear flag and just show error gracefully
          sessionStorage.removeItem(RELOAD_KEY);
        }
        throw error;
      }
      // Wait before retrying
      await new Promise((resolve) => setTimeout(resolve, retryDelay));
    }
  }
  // Should never reach here, but TypeScript needs it
  return importFn();
}
